/**
 * KB-3598 — the ONE home for every review-pattern deep link.
 *
 * Pure functions only. No React, no Firestore — so the shape every emitter (the inbox-docket script,
 * the change digest, Run details, the card's own copy-link control) produces is a claim a unit test
 * can check, not something four call sites each get to reinvent.
 *
 * ⚠️ Before this ticket there were TWO independently-typed URL builders for the same destination —
 * `scripts/camp-data/_operator/inbox-docket/renderDocket.cjs`'s `reviewerLink` and
 * `functions/src/camp-data/changeDigest.ts`'s inline template literal — plus a THIRD, dead one
 * (`functions/scripts/camp-data/_shared/triageRulesLib.cjs`'s `tab=review`, a route
 * `CampDataManager.tsx` has never served). A fourth copy is exactly how the second dead link would
 * have been born. AC5 — grep `selectedPatternId` and `reviewerLink` before adding a fifth.
 *
 * ── PARAM NAMING ─────────────────────────────────────────────────────────────────────────────────
 * `pattern` is the canonical param this ticket introduces (AC1). `selectedPatternId` is the pre-KB-3598
 * name and is kept as a READ-ONLY alias — every doc-comment, docket row and digest email already out in
 * the wild before this shipped still resolves. New links are always built with `pattern`; nothing
 * outside `readPatternIdParam` ever reads `selectedPatternId` again.
 */

/** The canonical query param naming a review pattern. New links use this one. */
export const PATTERN_ID_PARAM = "pattern";

/** KB-2055's original param name. Still read (never written) so every pre-KB-3598 link keeps working. */
export const PATTERN_ID_LEGACY_PARAM = "selectedPatternId";

/**
 * The full doc-id shape: a sha1 hex digest, `sha1("${campSlug}|${changeGroupKey}")`.
 * Exported so `isPatternIdPrefix` and the ambiguous-prefix Firestore range query (`campDataReviewInbox.ts`,
 * AC2) both anchor on the same definition of "hex" rather than each inventing a character class.
 */
export const PATTERN_DOC_ID_LENGTH = 40;
const HEX_RE = /^[0-9a-f]+$/i;

/** AC2 — the shortest prefix this surface treats as "specific enough" to look up. */
export const MIN_PATTERN_ID_PREFIX_LENGTH = 8;

/** A full, exact `patternDocId` — 40 lowercase-or-mixed hex chars. */
export function isFullPatternId(input: string): boolean {
  return input.length === PATTERN_DOC_ID_LENGTH && HEX_RE.test(input);
}

/**
 * AC2 — "an unambiguous prefix (≥8 hex)". Any hex string from `MIN_PATTERN_ID_PREFIX_LENGTH` up to
 * (and including) a full id counts as a prefix query candidate; `isFullPatternId` is checked first by
 * callers that want to skip the query and `getDoc` directly, so this does not exclude the full-length
 * case — a 40-char hex string is its own prefix.
 */
export function isPatternIdPrefixCandidate(input: string): boolean {
  return input.length >= MIN_PATTERN_ID_PREFIX_LENGTH && input.length <= PATTERN_DOC_ID_LENGTH && HEX_RE.test(input);
}

/**
 * The canonical review-pattern deep link (AC1, AC4, AC5).
 *
 * `baseUrl` defaults to prod because every non-test caller today (the docket script, the change
 * digest) runs OUTSIDE the browser and has no `window.location` to read — see `renderDocket.cjs`'s own
 * `reviewerLink`, which this supersedes and which carried the identical default for the identical
 * reason.
 */
export function buildReviewPatternLink(patternDocId: string, opts: { baseUrl?: string } = {}): string {
  const baseUrl = (opts.baseUrl ?? "https://pro.kidsbooked.com").replace(/\/+$/, "");
  const id = encodeURIComponent(String(patternDocId || ""));
  return `${baseUrl}/camp-data?tab=inbox&${PATTERN_ID_PARAM}=${id}`;
}

/**
 * Reads the pattern id off either param, canonical first.
 *
 * ⚠️ Never write `selectedPatternId` again — this is the one place that still reads it, and it exists
 * only so a link minted before this ticket keeps resolving. Every builder above writes `pattern`.
 */
export function readPatternIdParam(params: URLSearchParams): string | null {
  return params.get(PATTERN_ID_PARAM) ?? params.get(PATTERN_ID_LEGACY_PARAM);
}

/**
 * The params for "go to this pattern", preserving everything else. Always writes the CANONICAL param
 * and drops the legacy one — a URL this app builds itself should never carry both spellings of the
 * same fact, which is a stale-alias bug waiting to happen the moment only one of the two is updated.
 */
export function withPatternIdParam(params: URLSearchParams, patternDocId: string): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(PATTERN_ID_LEGACY_PARAM);
  next.set(PATTERN_ID_PARAM, patternDocId);
  return next;
}

/** The params for "clear the pattern filter" — drops BOTH spellings, since either may be present. */
export function withoutPatternIdParam(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(PATTERN_ID_PARAM);
  next.delete(PATTERN_ID_LEGACY_PARAM);
  return next;
}

/**
 * AC2 — the third lookup form: `campSlug` + `changeGroupKey`, the doc id's own preimage.
 *
 * `sha1("${campSlug}|${changeGroupKey}")` — the exact string the server hashes to mint the doc id
 * (`campDataReviewInbox.ts:52`, `derivePatternDocs.cjs`). Computed with Web Crypto rather than a hand
 * hex-encoder: `crypto.subtle.digest` is available in every browser this admin app targets (HTTPS
 * origin) and needs no dependency this bundle does not already ship.
 *
 * Async because `SubtleCrypto.digest` is — there is no synchronous sha1 in the browser. Callers that
 * need this to feed straight into `fetchReviewPatternById` (a `getDoc`, not a query) should `await` it.
 */
export async function derivePatternDocId(campSlug: string, changeGroupKey: string): Promise<string> {
  const preimage = `${campSlug}|${changeGroupKey}`;
  const bytes = new TextEncoder().encode(preimage);
  const digest = await crypto.subtle.digest("SHA-1", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * What the "Go to pattern" box (AC3) does with whatever the operator pasted.
 *
 * ⚠️ `campSlug|changeGroupKey` is accepted in exactly the preimage's own shape — a literal `|` between
 * the two — rather than a bespoke two-field form. Both values are already juxtaposed with that same
 * separator everywhere the pipeline prints them (`groupKeyText`, e.g. `lune-spark|price|...`), so the
 * one splittable, unambiguous read is "the LAST `|` divides camp from group key" — a `changeGroupKey`
 * is itself a hex digest (never contains `|`), but `campSlug` could in principle; splitting from the
 * right is the one direction that cannot misparse a slug containing the separator.
 */
export type PatternLookupInput =
  | { kind: "id"; patternId: string }
  | { kind: "prefix"; prefix: string }
  | { kind: "preimage"; campSlug: string; changeGroupKey: string }
  | { kind: "invalid"; reason: string };

export function parsePatternLookupInput(raw: string): PatternLookupInput {
  const trimmed = raw.trim();
  if (!trimmed) return { kind: "invalid", reason: "Enter a pattern id, an id prefix, or campSlug|changeGroupKey." };

  if (isFullPatternId(trimmed)) return { kind: "id", patternId: trimmed.toLowerCase() };
  if (isPatternIdPrefixCandidate(trimmed)) return { kind: "prefix", prefix: trimmed.toLowerCase() };

  const sep = trimmed.lastIndexOf("|");
  if (sep > 0 && sep < trimmed.length - 1) {
    const campSlug = trimmed.slice(0, sep);
    const changeGroupKey = trimmed.slice(sep + 1);
    return { kind: "preimage", campSlug, changeGroupKey };
  }

  return {
    kind: "invalid",
    reason: `Not a recognised pattern id, prefix (≥${MIN_PATTERN_ID_PREFIX_LENGTH} hex chars), or campSlug|changeGroupKey pair.`,
  };
}
